import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Dropdown, Nav } from "react-bootstrap";
import { favoritesAPI } from "@/api/favorites";
import { useFavorites } from "@/contexts/FavoritesContext";
import IcomoonIcon from "@icons/IcomoonIcon";
import AwesomeIcon from "@icons/AwesomeIcon";
import FavoriteButton from "@/components/FavoriteButton/FavoriteButton";
import SntImageView from "@/cleanup/widgets/images/SntImageView";
import GlobalUtils from "@/utils/GlobalUtils";
import GeozoneListImage from "@/pages/Geozone/Image/GeozoneListImage";
import {
  DeviceHomeRoute,
  InfraRoute,
} from "@/cleanup/utils/routesUtil/routesUtil";
import {
  Device,
  Geozone,
  Organization,
  FavoriteItems,
} from "@/types/favorites";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { useTranslateFunction } from "@/hooks/useLanguage";
import { RootState } from "@/types/RootState";

const FavoritesDropdown: React.FC = () => {
  const loginInfo = useSelector((state: RootState) => state.user);
  const { isMobile } = useSelector((state: RootState) => state.all);
  const translate = useTranslateFunction();
  const history = useHistory();
  const { favoritesByType } = useFavorites();

  const [favoriteItems, setFavoriteItems] = useState<FavoriteItems>({
    devices: [],
    geozones: [],
    organizations: [],
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [lastFavoritesHash, setLastFavoritesHash] = useState<string>("");
  const [hasLoadedOnce, setHasLoadedOnce] = useState<boolean>(false);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  // Check if user can see organizations (only SYSTEM and PARTNER users)
  const canViewOrganizations =
    loginInfo?.organisation?.organisationType === "SYSTEM" ||
    loginInfo?.organisation?.organisationType === "PARTNER";

  // Create a hash from favorites to detect changes
  const createFavoritesHash = useCallback((): string => {
    const deviceIds = Array.from(favoritesByType.device || []).sort();
    const organizationIds = Array.from(
      favoritesByType.organization || []
    ).sort();
    const geozoneIds = Array.from(favoritesByType.geozone || []).sort();
    return JSON.stringify({ deviceIds, organizationIds, geozoneIds });
  }, [favoritesByType]);

  const loadFavoriteItems = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await favoritesAPI.getAllFavoriteEntities();

      // Parse the JSON response since backend returns a JSON string
      const data =
        typeof response.data === "string"
          ? JSON.parse(response.data)
          : response.data;
      const { devices = [], organizations = [], geozones = [] } = data;

      // Backend now returns items sorted alphabetically by name
      setFavoriteItems({
        devices: devices,
        geozones: geozones,
        organizations: canViewOrganizations ? organizations : [],
      });

      setHasLoadedOnce(true);
    } catch (error) {
      console.error("Error loading favorite items:", error);
      setFavoriteItems({
        devices: [],
        geozones: [],
        organizations: [],
      });
    } finally {
      setLoading(false);
    }
  }, [canViewOrganizations]);

  const handleDeviceClick = (device: Device): void => {
    let url: string;
    if (device.deviceCategory === "INFRA") {
      url = InfraRoute.getUpdatePage({
        type: "view",
        id: device.id,
      });
    } else {
      url = DeviceHomeRoute.getUpdatePage({
        id: device.id,
        tabActive: "asset_details_tab",
      });
    }
    setShowDropdown(false);
    history.push(url);
  };

  const handleGeozoneClick = (geozone: Geozone): void => {
    setShowDropdown(false);
    history.push(`/geozone_individual?id=${geozone.id}`);
  };

  const handleOrganizationClick = (organization: Organization): void => {
    setShowDropdown(false);
    history.push(`/organization_update?type=view&org=${organization.id}`);
  };

  // Handle favorite toggle callbacks
  const handleDeviceFavoriteToggle = (
    isFavorite: boolean,
    entityType: string,
    entityId: string
  ): void => {
    if (!isFavorite) {
      // Remove from local state when unfavorited
      setFavoriteItems((prev) => ({
        ...prev,
        devices: prev.devices.filter(
          (device) => device.id.toString() !== entityId
        ),
      }));
    }
  };

  const handleGeozoneFavoriteToggle = (
    isFavorite: boolean,
    entityType: string,
    entityId: string
  ): void => {
    if (!isFavorite) {
      // Remove from local state when unfavorited
      setFavoriteItems((prev) => ({
        ...prev,
        geozones: prev.geozones.filter(
          (geozone) => geozone.id.toString() !== entityId
        ),
      }));
    }
  };

  const handleOrganizationFavoriteToggle = (
    isFavorite: boolean,
    entityType: string,
    entityId: string
  ): void => {
    if (!isFavorite) {
      // Remove from local state when unfavorited
      setFavoriteItems((prev) => ({
        ...prev,
        organizations: prev.organizations.filter(
          (org) => org.id.toString() !== entityId
        ),
      }));
    }
  };

  const hasAnyFavorites =
    favoriteItems.devices.length > 0 ||
    favoriteItems.geozones.length > 0 ||
    favoriteItems.organizations.length > 0;

  const handleDropdownToggle = (isOpen: boolean): void => {
    setShowDropdown(isOpen);
    // Load favorites on first open or refresh if favorites have changed
    if (isOpen) {
      if (!hasLoadedOnce) {
        // First time opening - load favorites
        loadFavoriteItems().then(() => {
          setLastFavoritesHash(createFavoritesHash());
        });
      } else {
        // Check if favorites have changed
        const currentHash = createFavoritesHash();
        if (currentHash !== lastFavoritesHash) {
          loadFavoriteItems().then(() => {
            setLastFavoritesHash(currentHash);
          });
        }
      }
    }
  };

  return (
    <Dropdown align="end" onToggle={handleDropdownToggle} show={showDropdown}>
      <Dropdown.Toggle
        as={Nav.Item}
        id="favorites-dropdown"
        title={translate(LanguageKey.favorites_key)}
        role="button"
        style={{
          padding: "0.5rem",
          fontSize: 24,
          height: 49,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        className="d-flex justify-content-center align-items-center"
        to="/#"
      >
        <style>
          {`
            #favorites-dropdown::after {
              display: none !important;
            }
          `}
        </style>
        <IcomoonIcon
          icon="star-empty"
          size={24}
          role="button"
          style={{
            marginTop: "-4px",
            color: "currentColor",
          }}
        />
      </Dropdown.Toggle>

      <Dropdown.Menu
        style={{
          minWidth: "350px",
          maxWidth: "400px",
          padding: "0",
          borderRadius: "8px",
          overflow: "hidden",
          ...(isMobile && { left: "-190px" }),
        }}
      >
        <div className="p-3 bg-light border-bottom">
          <h6 className="mb-0 text-muted fw-bold">
            {translate(LanguageKey.favorites_key)}
          </h6>
        </div>

        {loading ? (
          <div className="p-3 text-center">
            <div className="d-flex align-items-center justify-content-center">
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              />
              <small className="text-muted">
                {translate(LanguageKey.loading_key)}
              </small>
            </div>
          </div>
        ) : !hasAnyFavorites ? (
          <div className="p-4 text-center">
            <div className="text-muted">
              <IcomoonIcon
                icon="star-empty"
                size="24"
                className="mb-2 d-block"
              />
              <small>{translate(LanguageKey.no_favorites_found_key)}</small>
            </div>
          </div>
        ) : (
          <div style={{ height: "80vh", overflowY: "auto" }}>
            {/* Devices Section */}
            {favoriteItems.devices.length > 0 && (
              <div className="px-3 py-2">
                <div className="d-flex align-items-center mb-2">
                  <IcomoonIcon
                    icon="device"
                    size="14"
                    className="me-2 text-primary"
                  />
                  <span
                    className="fw-bold text-muted"
                    style={{ fontSize: "16px" }}
                  >
                    {translate(LanguageKey.assets_key)} (
                    {favoriteItems.devices.length})
                  </span>
                </div>
                {favoriteItems.devices.map((device) => (
                  <div
                    key={`device-${device.id}`}
                    onClick={() => handleDeviceClick(device)}
                    className="d-flex align-items-center p-2 mb-1 cursor-pointer"
                    style={{
                      cursor: "pointer",
                      overflow: "hidden",
                    }}
                    onMouseEnter={(e) => {
                      const div = (e.target as HTMLElement).closest("div");
                      if (div) div.style.backgroundColor = "#f8f9fa";
                    }}
                    onMouseLeave={(e) => {
                      const div = (e.target as HTMLElement).closest("div");
                      if (div) div.style.backgroundColor = "transparent";
                    }}
                  >
                    <div
                      className="me-3"
                      style={{ width: "30px", height: "30px", flexShrink: 0 }}
                    >
                      <SntImageView
                        src={GlobalUtils.getFile(
                          "device",
                          device.id,
                          "image",
                          device.trackerIcon,
                          "small"
                        )}
                        alt={device.name}
                        width="30"
                        height="30"
                        size="30"
                      />
                    </div>
                    <div
                      className="flex-grow-1 min-width-0"
                      style={{ overflow: "hidden" }}
                    >
                      <div
                        className="text-truncate"
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          lineHeight: "1.2",
                        }}
                      >
                        {device.name}
                      </div>
                      <p
                        className="subtitle-text text-muted text-truncate mb-0"
                        style={{
                          fontSize: "12px",
                          margin: 0,
                          lineHeight: "1.2",
                        }}
                      >
                        {device.serial ? device.serial : device.name}
                      </p>
                    </div>
                    <div className="ms-2" style={{ flexShrink: 0 }}>
                      <FavoriteButton
                        entityType="device"
                        entityId={device.id}
                        onToggle={handleDeviceFavoriteToggle}
                        size="sm"
                        tooltip={false}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Geozones Section */}
            {favoriteItems.geozones.length > 0 && (
              <div className="px-3 py-2">
                <div className="d-flex align-items-center mb-2">
                  <IcomoonIcon
                    icon="map-marker"
                    size="14"
                    className="me-2 text-primary"
                  />
                  <span
                    className="fw-bold text-muted"
                    style={{ fontSize: "16px" }}
                  >
                    {translate(LanguageKey.geozones_key)} (
                    {favoriteItems.geozones.length})
                  </span>
                </div>
                {favoriteItems.geozones.map((geozone) => (
                  <div
                    key={`geozone-${geozone.id}`}
                    onClick={() => handleGeozoneClick(geozone)}
                    className="d-flex align-items-center p-2 mb-1 cursor-pointer"
                    style={{
                      cursor: "pointer",
                      overflow: "hidden",
                    }}
                    onMouseEnter={(e) => {
                      const div = (e.target as HTMLElement).closest("div");
                      if (div) div.style.backgroundColor = "#f8f9fa";
                    }}
                    onMouseLeave={(e) => {
                      const div = (e.target as HTMLElement).closest("div");
                      if (div) div.style.backgroundColor = "transparent";
                    }}
                  >
                    <div
                      className="me-3"
                      style={{ width: "30px", height: "30px", flexShrink: 0 }}
                    >
                      <GeozoneListImage
                        className=""
                        image={geozone.image}
                        icon={geozone.icon}
                        width="30"
                        height="30"
                      />
                    </div>
                    <div
                      className="flex-grow-1 min-width-0"
                      style={{ overflow: "hidden" }}
                    >
                      <div
                        className="text-truncate"
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          lineHeight: "1.2",
                        }}
                      >
                        {geozone.name}
                      </div>
                      <p
                        className="subtitle-text text-muted text-truncate mb-0"
                        style={{
                          fontSize: "12px",
                          margin: 0,
                          lineHeight: "1.2",
                        }}
                      >
                        {geozone.address || geozone.name}
                      </p>
                    </div>
                    <div className="ms-2" style={{ flexShrink: 0 }}>
                      <FavoriteButton
                        entityType="geozone"
                        entityId={geozone.id}
                        onToggle={handleGeozoneFavoriteToggle}
                        size="sm"
                        tooltip={false}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Organizations Section */}
            {favoriteItems.organizations.length > 0 && (
              <div className="px-3 py-2">
                <div className="d-flex align-items-center mb-2">
                  <IcomoonIcon
                    icon="building"
                    size="14"
                    className="me-2 text-primary"
                  />
                  <span
                    className="fw-bold text-muted"
                    style={{ fontSize: "16px" }}
                  >
                    {translate(LanguageKey.organizations_key)} (
                    {favoriteItems.organizations.length})
                  </span>
                </div>
                {favoriteItems.organizations.map((organization) => (
                  <div
                    key={`org-${organization.id}`}
                    onClick={() => handleOrganizationClick(organization)}
                    className="d-flex align-items-center p-2 mb-1 cursor-pointer"
                    style={{
                      cursor: "pointer",
                      overflow: "hidden",
                    }}
                    onMouseEnter={(e) => {
                      const div = (e.target as HTMLElement).closest("div");
                      if (div) div.style.backgroundColor = "#f8f9fa";
                    }}
                    onMouseLeave={(e) => {
                      const div = (e.target as HTMLElement).closest("div");
                      if (div) div.style.backgroundColor = "transparent";
                    }}
                  >
                    <div
                      className="me-3"
                      style={{ width: "30px", height: "30px", flexShrink: 0 }}
                    >
                      <AwesomeIcon icon="user-group" size="2xl" />
                    </div>
                    <div
                      className="flex-grow-1 min-width-0"
                      style={{ overflow: "hidden" }}
                    >
                      <div
                        className="text-truncate"
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          lineHeight: "1.2",
                        }}
                      >
                        {organization.name}
                      </div>
                      <p
                        className="subtitle-text text-muted text-truncate mb-0"
                        style={{
                          fontSize: "12px",
                          margin: 0,
                          lineHeight: "1.2",
                        }}
                      >
                        {organization.status || organization.name}
                      </p>
                    </div>
                    <div className="ms-2" style={{ flexShrink: 0 }}>
                      <FavoriteButton
                        entityType="organization"
                        entityId={organization.id}
                        onToggle={handleOrganizationFavoriteToggle}
                        size="sm"
                        tooltip={false}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default FavoritesDropdown;
